import { useState, useEffect, useRef, useCallback } from 'react';
import { manufacturingStats } from '@/mocks/homeData';

function CountUpNumber({ end, suffix = '', label, icon, startCounting }: { end: number; suffix?: string; label: string; icon: string; startCounting: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startCounting) return;

    const duration = 2000;
    let startTime: number | null = null;
    let frame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      }
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [end, startCounting]);

  const formatNumber = (n: number) => {
    if (n >= 1000) {
      return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'K';
    }
    return n.toString();
  };

  const displayBase = end >= 1000 ? Math.floor(end / 1000) : end;
  const displayValue = end >= 1000 ? formatNumber(end) : count.toString();

  return (
    <div className="text-center p-6">
      <div className="w-10 h-10 mx-auto rounded-lg bg-primary-50 flex items-center justify-center mb-4">
        <i className={`${icon} text-primary-500 text-lg`}></i>
      </div>
      <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1.5">
        {startCounting ? (end >= 1000 ? displayValue : count + suffix) : '0' + suffix}
      </div>
      <div className="text-xs text-foreground-500 leading-snug">{label}</div>
    </div>
  );
}

export default function Capabilities() {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const checkVisibility = useCallback(() => {
    if (hasAnimated || !sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.7) {
      setHasAnimated(true);
    }
  }, [hasAnimated]);

  useEffect(() => {
    checkVisibility();
    window.addEventListener('scroll', checkVisibility, { passive: true });
    return () => window.removeEventListener('scroll', checkVisibility);
  }, [checkVisibility]);

  return (
    <section id="capabilities" ref={sectionRef} className="py-20 md:py-28 bg-white">
      <div className="section-container">
        <div className="text-center mb-14 animate-on-scroll">
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-5">
            Manufacturing Capabilities
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-800 mb-3">
            Built to Deliver<br />at Global Scale
          </h2>
          <p className="text-foreground-500 max-w-[560px] mx-auto leading-relaxed">
            Our manufacturing infrastructure is designed for volume, precision, and flexibility — capable of handling complex orders with consistent quality.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#E6EAF0] rounded-lg overflow-hidden bg-white">
          {manufacturingStats.map((stat, i) => (
            <div
              key={i}
              className={`${i % 2 === 1 ? 'border-l border-[#E6EAF0]' : ''} ${i >= 4 ? 'border-t border-[#E6EAF0]' : ''} ${i % 2 === 0 && i >= 4 ? '' : ''}`}
            >
              <CountUpNumber
                end={stat.value}
                suffix={stat.suffix}
                label={stat.label}
                icon={stat.icon}
                startCounting={hasAnimated}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}